import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronRight, X } from 'lucide-react';
import Button from '@/components/ui/Button';

interface TextAreaProps {
  text?: string;
  setText: (text: string) => void;
  placeholder: string;
  maxLength: number;
  name: string;
  handleKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  className?: string;
}

export const TextArea = ({
  text,
  setText,
  placeholder,
  maxLength,
  name,
  handleKeyDown,
  className,
}: TextAreaProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '160px'; // 160px
      const scrollHeight = textAreaRef.current.scrollHeight;
      textAreaRef.current.style.height = `${Math.min(scrollHeight, 320)}px`; // 320px
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    adjustTextareaHeight();
  };

  useEffect(() => {
    adjustTextareaHeight();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <div>
      <textarea
        ref={textAreaRef}
        className={`p-3 rounded-md w-full placeholder-zinc-500 focus:outline-none border border-zinc-700 bg-zinc-950 overflow-y-auto resize-none ${className || ''}`}
        value={text}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={handleTextChange}
        name={name}
        id={name}
        onKeyDown={() => handleKeyDown && handleKeyDown}
      />
    </div>
  );
};

interface Props {
  text?: string;
  setText: (text: string) => void;
  placeholder: string;
  label: string;
  title: string;
  maxLength: number;
  name: string;
  required?: boolean;
}

export const TextAreaModal = ({
  text,
  setText,
  placeholder,
  label,
  title,
  maxLength,
  name,
  required = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalText, setInternalText] = useState(text || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const isDifferent = text !== internalText;

  useEffect(() => {
    setInternalText(text || '');
  }, [text]);

  const toggleModal = () => {
    if (isOpen) {
      setInternalText(text || '');
    }
    setIsOpen(!isOpen);
  };

  const handleSave = () => {
    setText(internalText);
    toggleModal();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  useEffect(() => {
    if (isOpen) textareaRef.current?.focus();

    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';

    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.body.style.overflow = 'auto';
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div>
      <button
        onClick={toggleModal}
        className="py-3 px-4 rounded-md w-full focus:outline-none flex items-center justify-between border border-zinc-700"
      >
        <div className="flex gap-4">
          {!!text && <Check />}
          <p>{label}</p>
        </div>
        <div className="flex gap-4">
          <p className="text-zinc-400 overflow-ellipsis text-nowrap overflow-hidden max-w-20 sm:max-w-52">
            {text || ''}
          </p>
          <ChevronRight />
        </div>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-40">
          <div
            ref={modalRef}
            className="text-white w-full max-w-xl rounded-xl shadow-xl flex flex-col p-4 sm:p-6 lg:p-8 gap-8"
          >
            <div className="flex justify-between items-center border-zinc-700">
              <h2 className="text-xl font-semibold">{title}</h2>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="flex flex-col gap-8">
              <TextArea
                text={internalText}
                setText={setInternalText}
                placeholder={placeholder}
                maxLength={maxLength}
                name={name}
                handleKeyDown={handleKeyDown}
              />
              <Button
                variant="slim"
                isPrimary={true}
                disabled={(required && !internalText) || !isDifferent}
                onClick={handleSave}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
